import React, {
  useState,
  useCallback,
  useEffect,
  Fragment,
  useContext,
} from "react";

import {
  EntryForm,
  FormCard,
  Section,
  Header,
  SectionNumber,
  Title,
  SectionHint,
  Group,
  GroupTitle,
  ProjectRow,
  ProjectFields,
  Divider,
} from "./styles";

import {
  InputField,
  CheckBoxField,
  SelectField,
  //AutoCompleteField,
} from "basics";

import { ImageUploader } from "components/ImageUploader";

import VideoPlayer from "components/VideoPlayer";

import FileUploader from "components/FileUploader";

import Questionnaire from "components/Questionnaire";

import { ComponentContainer, ComponentTitle } from "App/styles";

import { useForm, useFieldArray } from "react-hook-form";

import FormLoader from "loader/FormLoader";

import { Grid, Col, Row } from "react-styled-flexboxgrid";

import useEntryForm from "hooks/useEntryForm";

import useScroll from "hooks/useScroll";

import FormToolBar from "components/FormToolBar";

import { FormStatus } from "model/dictionary";

import { AlertContext } from "contexts/AlertContext";

import { States, StaffPositions } from "model/options";
import FacebookLineIcon from "icons/FacebookLineIcon";
import LinkedinLineIcon from "icons/LinkedinLineIcon";
import InstagramLineIcon from "icons/InstagramLineIcon";
import TwitterLineIcon from "icons/TwitterLineIcon";
import WebLineIcon from "icons/WebLineIcon";
import PhoneLineIcon from "icons/PhoneLineIcon";
import Survey from "components/Survey";
import { object } from "yup";
import { yupResolver } from "@hookform/resolvers";

// El Grid de react-styled-flexboxgrid sólo aporta las canaletas de las filas:
// el aire de la sección lo pone `Section`.
const gridStyle = { width: "100%", padding: 0 };

export default function Form({
  onCompleted,
  onClose,
  form,
  loading,
  readOnly,
  currentStatus,
  // La postulación abierta desde el listado: el modal ya pone el premio en su
  // cabecera fija y ya es el marco de la tarjeta.
  inModal,
}) {
  const [showPostulator, setShowPostulator] = useState(
    (form && form.showPostulator) || false
  );

  const [showSurvey, setShowSurvey] = useState(false);

  const { SaveForm, UpdateForm, schema } = useEntryForm();
  const { scrollRef, scrollToRef } = useScroll();
  const { showWaiting, hideWaiting } = useContext(AlertContext);

  const { register, handleSubmit, errors, setValue, reset, control, getValues } =
    useForm({
      resolver: yupResolver(object(schema)),
      defaultValues: form,
      mode: "onSubmit",
      reValidateMode: "onSubmit",
    });

  // Adjuntos con subida en curso: mientras haya alguno pendiente bloqueamos el
  // envío para no guardar la postulación con archivos a medio subir.
  const [pendingUploads, setPendingUploads] = useState({});

  const setPending = useCallback((fieldId, pending) => {
    setPendingUploads((prev) => {
      if (!!prev[fieldId] === pending) return prev;
      return { ...prev, [fieldId]: pending };
    });
  }, []);

  const uploading = Object.values(pendingUploads).some(Boolean);

  const { fields } = useFieldArray({
    control,
    name: "sections",
    keyName: "_rhfId",
  });

  useEffect(() => {
    scrollToRef();
  }, [scrollToRef]);

  useEffect(() => {
    reset(form);
  }, [form, reset]);

  const onFormSubmitted = (data) => {
    setShowSurvey(false);
    onCompleted();
  };

  const mergeSectionsWithMetadata = (rhfData, originalSections) => {
    if (!originalSections) return rhfData;

    const mergedSections = originalSections.map((origSection, sIdx) => {
      const dataSection = rhfData.sections && rhfData.sections[sIdx];

      return {
        ...origSection,
        questions: origSection.questions.map((origQuestion, qIdx) => {
          const dataQuestion =
            dataSection &&
            dataSection.questions &&
            dataSection.questions[qIdx];

          const merged = {
            ...origQuestion,
            value:
              dataQuestion && dataQuestion.value !== undefined
                ? dataQuestion.value
                : origQuestion.value,
          };

          if (origQuestion.questionType === "multiple" && origQuestion.options) {
            merged.options = origQuestion.options.map((origOpt, oIdx) => {
              const dataOpt =
                dataQuestion &&
                dataQuestion.options &&
                dataQuestion.options[oIdx];

              return {
                ...origOpt,
                checked:
                  dataOpt && dataOpt.checked !== undefined
                    ? dataOpt.checked
                    : origOpt.checked || false,
              };
            });
          }

          return merged;
        }),
      };
    });

    return { ...rhfData, sections: mergedSections };
  };

  const onSubmit = (data) => {
    // En la confirmación (draft) el guardado real lo disparan los botones
    // Confirmar/Descartar (onUpdateForm); un submit implícito por Enter no debe
    // reenviar el mail ni recrear el borrador.
    if (isPendingConfirmation) {
      return;
    }

    showWaiting("Actualizando formulario...");

    window.scrollTo(0, scrollRef.current.offsetTop);

    form.email = data.email;

    const mergedData = mergeSectionsWithMetadata(data, form.sections);

    SaveForm(mergedData, form, FormStatus.DRAFT).then(() => {
      hideWaiting();

      if (form.hasSurvey) {
        setShowSurvey(true);

        return;
      }

      onCompleted();
    });
  };

  const onUpdateForm = (nextStatus) => {
    showWaiting("Actualizando formulario...");

    window.scrollTo(0, scrollRef.current.offsetTop);

    // En la confirmación desde el link del mail la postulación está en `draft`
    // y el formulario queda editable: el postulante puede corregir cualquier
    // dato antes de confirmar. Fusionamos los valores vigentes sobre `form`
    // —que conserva competition/award/topic/pdf/entryNumber/etc., campos que
    // no son inputs— y restauramos los hidden estructurales (`status` es un
    // array que el input serializaría a string y rompería `setUpFormStatus`).
    // No se regenera el PDF ni se reenvían mails: UpdateForm sólo reescribe el
    // documento con el nuevo estado.
    let formToSave = form;

    if (isPendingConfirmation) {
      const mergedData = mergeSectionsWithMetadata(getValues(), form.sections);

      formToSave = {
        ...form,
        ...mergedData,
        status: form.status,
        minifiedHeader: form.minifiedHeader,
        hasSurvey: form.hasSurvey,
      };
    }

    UpdateForm(formToSave, nextStatus).then(() => {
      hideWaiting();

      onCompleted(nextStatus);
    });
  };

  const handleShowPostulator = useCallback((value) => {
    setShowPostulator(value);
  }, []);

  const formTitle = form.award && form.award.title ? form.award.title : " ";

  const showVideoLink = form.award.id !== "vBmio3SvKAfpvMP8bXNn";

  // En la vista de lectura sin logo cargado no se dibuja el círculo, así que
  // el grupo Proyecto pasa a una sola columna: si no, la grilla dejaba la
  // columna del avatar vacía y apretaba los campos contra el borde.
  const showAvatar = !readOnly || Boolean(form.avatar);

  // La postulación en `draft` se abre desde el link del mail con todos los
  // campos editables: el postulante puede corregir cualquier dato antes de
  // confirmar. El guardado lo dispara el botón "Confirmar postulación"
  // (onUpdateForm), no el submit del formulario.
  const isPendingConfirmation = currentStatus === FormStatus.DRAFT;

  return (
    <>
      {showSurvey && (
        <Survey
          onClose={onFormSubmitted}
          showSurvey={showSurvey}
          email={form.email}
        />
      )}
      <ComponentContainer
        ref={scrollRef}
        style={{
          marginTop: "0px",
          position: "relative",
          padding: inModal ? "0" : undefined,
        }}
      >
        {!inModal && <ComponentTitle>{formTitle}</ComponentTitle>}

        <EntryForm onSubmit={handleSubmit(onSubmit)}>
          <FormCard $readOnly={readOnly} $flat={inModal}>
            <Section>
              <Header>
                <SectionNumber>1</SectionNumber>
                <Title>Datos de la postulación</Title>
                <SectionHint>
                  {isPendingConfirmation
                    ? "Revisá tus datos y corregí lo que haga falta antes de confirmar la postulación."
                    : readOnly
                    ? "Quién postuló y qué proyecto."
                    : "Quién postula y qué proyecto. Los campos con * son obligatorios."}
                </SectionHint>
              </Header>

              {loading ? (
                <FormLoader />
              ) : (
                <Grid style={gridStyle} className="form-body">
                  <input
                    type="hidden"
                    ref={register}
                    id="identity"
                    name="identity"
                    value={form.identity}
                  />
                  <input
                    type="hidden"
                    ref={register}
                    id="status"
                    name="status"
                    value={form.status}
                  />

                  <input
                    type="hidden"
                    ref={register}
                    id="minifiedHeader"
                    name="minifiedHeader"
                    value={form.minifiedHeader || false}
                  />

                  <input
                    type="hidden"
                    ref={register}
                    id="hasSurvey"
                    name="hasSurvey"
                    value={form.hasSurvey}
                  />

                  <Group>
                    <GroupTitle>Proyecto</GroupTitle>
                    <ProjectRow $withAvatar={showAvatar}>
                      <ImageUploader
                        register={register}
                        id="avatar"
                        value={form.avatar}
                        error={errors.avatar}
                        setValue={setValue}
                        readOnly={readOnly}
                      />
                      <ProjectFields>
                        <Row>
                          <Col xs={12} className="print-keep">
                            <InputField
                              label="Nombre del Proyecto"
                              type="text"
                              id="projectName"
                              register={register}
                              error={errors.projectName}
                              //value={form.projectName}
                              required={!form.minifiedHeader}
                              readOnly={readOnly}
                              tabIndex="1"
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            xs={12}
                            sm={form.minifiedHeader ? 12 : 6}
                            className="print-keep"
                          >
                            <InputField
                              label="Empresa/Institución"
                              type="text"
                              id="companyName"
                              register={register}
                              error={errors.companyName}
                              //value={form.companyName}
                              required={true}
                              readOnly={readOnly}
                              tabIndex="2"
                            />
                          </Col>
                          {!form.minifiedHeader && (
                            <Col xs={12} sm={6}>
                              <InputField
                                label="Sitio Web"
                                id="website"
                                type="link"
                                register={register}
                                error={errors.website}
                                value={readOnly && form.website}
                                required={false}
                                readOnly={readOnly}
                                tabIndex="3"
                                prefix="https://"
                                icon={<WebLineIcon />}
                              />
                            </Col>
                          )}
                        </Row>
                      </ProjectFields>
                    </ProjectRow>
                  </Group>

                  <Group>
                    <GroupTitle>Responsable</GroupTitle>
                    <Row>
                      <Col xs={12} sm={6} className="print-keep">
                        <InputField
                          label="Nombre"
                          type="text"
                          id="firstName"
                          register={register}
                          error={errors.firstName}
                          required={true}
                          //value={form.firstName}
                          readOnly={readOnly}
                          tabIndex="4"
                        />
                      </Col>
                      <Col xs={12} sm={6} className="print-keep">
                        <InputField
                          label="Apellido"
                          type="text"
                          id="lastName"
                          register={register}
                          error={errors.lastName}
                          required={true}
                          //value={form.lastName}
                          readOnly={readOnly}
                          tabIndex="5"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} sm={6} className="print-keep">
                        <InputField
                          label="Email"
                          id="email"
                          type="email"
                          register={register}
                          error={errors.email}
                          //value={form.email}
                          required={true}
                          readOnly={readOnly}
                          tabIndex="6"
                        />
                      </Col>
                      {!readOnly && !isPendingConfirmation && (
                        <Col xs={12} sm={6}>
                          <InputField
                            label="Confirmación Email"
                            id="confirmEmail"
                            type="email"
                            register={register}
                            error={errors.confirmEmail}
                            required={true}
                            readOnly={readOnly}
                            tabIndex="7"
                            cancelPaste={true}
                            /* El bloqueo de pegado sin aviso parece un campo
                               roto: el placeholder lo explica antes. */
                            placeholder="reescriba el email (no se puede pegar)"
                          />
                        </Col>
                      )}
                    </Row>
                    <Row>
                      <Col xs={12} sm={6}>
                        <SelectField
                          options={StaffPositions}
                          id="staffPosition"
                          label="Cargo"
                          //value={form && form.staffPosition}
                          defaultOption="Elija un cargo"
                          register={register}
                          error={errors.staffPosition}
                          readOnly={readOnly}
                          tabIndex="8"
                        />
                      </Col>
                      {!form.minifiedHeader && (
                        <Col xs={12} sm={6}>
                          <InputField
                            label="Teléfono"
                            type="text"
                            id="phone"
                            register={register}
                            error={errors.phone}
                            //value={form.phone}
                            required={false}
                            readOnly={readOnly}
                            tabIndex="9"
                            icon={<PhoneLineIcon />}
                          />
                        </Col>
                      )}
                    </Row>
                  </Group>

                  {!form.minifiedHeader && (
                    <>
                      <Group>
                        <GroupTitle>Ubicación</GroupTitle>
                        <Row>
                          <Col xs={12} sm={6}>
                            <SelectField
                              options={States}
                              id="state"
                              label="Provincia"
                              //value={form && form.state}
                              defaultOption="Elija una provincia"
                              register={register}
                              error={errors.state}
                              readOnly={readOnly}
                              required={!form.minifiedHeader}
                              tabIndex="10"
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <InputField
                              label="Localidad"
                              type="text"
                              id="city"
                              register={register}
                              error={errors.city && errors.city}
                              //value={form && (form.city.label || form.city)}
                              required={!form.minifiedHeader}
                              readOnly={readOnly}
                              tabIndex="11"
                            />
                          </Col>
                        </Row>
                      </Group>

                      <Group>
                        <GroupTitle>
                          Redes <small>· opcional</small>
                        </GroupTitle>
                        <Row>
                          <Col xs={12} sm={6}>
                            <InputField
                              label="LinkedIn"
                              type="link"
                              id="linkedin"
                              register={register}
                              error={errors.linkedin}
                              value={readOnly && form.linkedin}
                              required={false}
                              readOnly={readOnly}
                              tabIndex="12"
                              //prefix="https://www.linkedin.com/"
                              icon={<LinkedinLineIcon />}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <InputField
                              label="Instagram"
                              type="link"
                              id="instagram"
                              register={register}
                              error={errors.instagram}
                              value={readOnly && form.instagram}
                              required={false}
                              readOnly={readOnly}
                              tabIndex="13"
                              //prefix="https://www.instagram.com/"
                              icon={<InstagramLineIcon />}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={12} sm={6}>
                            <InputField
                              label="Facebook"
                              type="link"
                              id="facebook"
                              register={register}
                              error={errors.facebook}
                              value={readOnly && form.facebook}
                              required={false}
                              readOnly={readOnly}
                              tabIndex="14"
                              //prefix="https://www.facebook.com/"
                              icon={<FacebookLineIcon />}
                            />
                          </Col>
                          <Col xs={12} sm={6}>
                            <InputField
                              label="X"
                              id="twitter"
                              type="link"
                              register={register}
                              error={errors.twitter}
                              value={readOnly && form.twitter}
                              required={false}
                              readOnly={readOnly}
                              tabIndex="15"
                              //prefix="https://twitter.com/"
                              icon={<TwitterLineIcon />}
                            />
                          </Col>
                        </Row>
                      </Group>

                      <Group className="print-hide">
                        {(!readOnly || !showPostulator) && <Divider />}
                        {!readOnly && (
                          <Fragment>
                            <CheckBoxField
                              id="showPostulator"
                              label="¿Estás postulando a alguien?"
                              secondary="Si es así, vas a tener que completar sus datos de contacto."
                              onChange={handleShowPostulator}
                              error={errors.showPostulator}
                              register={register}
                              checked={form.showPostulator}
                              showRow={true}
                              readOnly={readOnly}
                              tabIndex="25"
                            />
                          </Fragment>
                        )}
                        {(form.showPostulator || showPostulator) && (
                          <Row>
                            <Col xs={12} md={6} lg={4}>
                              <InputField
                                label="Nombre y Apellido"
                                type="text"
                                id="postulatorFullName"
                                error={errors.postulatorFullName}
                                //value={form.postulatorFullName}
                                register={register}
                                required={showPostulator}
                                readOnly={readOnly}
                                tabIndex="26"
                              />
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                              <InputField
                                label="Email"
                                type="email"
                                id="postulatorEmail"
                                register={register}
                                required={showPostulator}
                                error={errors.postulatorEmail}
                                //value={form.postulatorEmail}
                                readOnly={readOnly}
                                tabIndex="27"
                              />
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                              <InputField
                                label="Teléfono"
                                type="text"
                                id="postulatorPhone"
                                register={register}
                                required={false}
                                error={errors.postulatorPhone}
                                //value={form.postulatorPhone}
                                readOnly={readOnly}
                                tabIndex="28"
                              />
                            </Col>
                          </Row>
                        )}
                      </Group>
                    </>
                  )}
                </Grid>
              )}
            </Section>

            <Section>
              <Header>
                <SectionNumber>2</SectionNumber>
                <Title>Cuestionario</Title>
                <SectionHint>
                  {readOnly
                    ? "Las respuestas del premio, más los archivos y el video que se sumaron."
                    : "Las preguntas del premio, más los archivos y el video que quieras sumar."}
                </SectionHint>
              </Header>

              <Grid style={gridStyle} className="form-body">
                <Row>
                  <Col xs={12}>
                    <Questionnaire
                      sections={fields}
                      setValue={setValue}
                      register={register}
                      errors={errors}
                      readOnly={readOnly}
                      tabIndex="29"
                    />
                  </Col>
                </Row>
                {(!readOnly ||
                  (form.attachments && form.attachments.length > 0)) && (
                  <Group>
                    <GroupTitle>
                      Archivos adicionales{" "}
                      {!readOnly && <small>· opcional</small>}
                    </GroupTitle>
                    <Row>
                      <Col xs={12}>
                        <FileUploader
                          id="attachments"
                          folder={form && `attachments/${form.identity}`}
                          register={register}
                          error={errors.attachments}
                          value={form.attachments}
                          setValue={setValue}
                          readOnly={readOnly}
                          multiple={true}
                          onPendingChange={setPending}
                          tabIndex="200"
                        />
                      </Col>
                    </Row>
                  </Group>
                )}
                {(!readOnly || form.videoLink) && showVideoLink && (
                  <Group>
                    <GroupTitle>
                      Video elevator pitch{" "}
                      {!readOnly && <small>· opcional</small>}
                    </GroupTitle>
                    <Row>
                      <Col xs={12}>
                        <VideoPlayer
                          register={register}
                          id="videoLink"
                          error={errors.videoLink}
                          value={form.videoLink}
                          readOnly={readOnly}
                          tabIndex="201"
                        />
                      </Col>
                    </Row>
                  </Group>
                )}

                <Divider />

                <FormToolBar
                  onUpdateForm={onUpdateForm}
                  onClose={onClose}
                  currentStatus={currentStatus}
                  uploading={uploading}
                />
              </Grid>
            </Section>
          </FormCard>
        </EntryForm>
      </ComponentContainer>
    </>
  );
}
